import { useState, createContext, useContext } from 'react'
import useLoggedInUser from '~/hooks/useLoggedInUser'
import Router from 'next/router'
import { track } from '~/utils/analytics'
import { LOCALE_BAR_DISMISSED } from '~/lib/tracking-events'
import { getBrowserLocale } from '~/utils/localization'
import { getCookie, setCookie } from 'cookies-next'
import { YEAR_IN_SECONDS } from '~/lib/timeInSeconds'

export const LocaleBarContext = createContext()

const LocaleBarProvider = ({ children, language }) => {
  const { loggedInUser } = useLoggedInUser()
  const browserLanguage = getBrowserLocale()
  const userLanguage = loggedInUser?.locale?.split('_')[0] || null
  const browserLanguageContainsSystemLanguage = browserLanguage.indexOf(language) !== -1
  const hasCookie = getCookie(process.env.NEXT_PUBLIC_LOCALE_NOTIFICATION_COOKIE_NAME) === '1'
  const initialShow =
    browserLanguage !== language &&
    userLanguage !== language &&
    !browserLanguageContainsSystemLanguage &&
    !hasCookie

  const [visible, setVisible] = useState(initialShow)
  const show = () => setVisible(true)
  const hide = () => {
    if (!visible) return

    track(LOCALE_BAR_DISMISSED)
    setCookie(process.env.NEXT_PUBLIC_LOCALE_NOTIFICATION_COOKIE_NAME, 1, {
      secure: true,
      sameSite: 'lax',
      maxAge: YEAR_IN_SECONDS,
    })
    setVisible(false)
  }

  Router.events.on('routeChangeStart', () => {
    // Hide locale-bar on subsequent page-views
    if (!hasCookie) {
      hide()
    }
  })

  return (
    <LocaleBarContext.Provider value={{ visible, show, hide, hasCookie }}>
      {children}
    </LocaleBarContext.Provider>
  )
}

export const useLocaleBar = () => {
  const context = useContext(LocaleBarContext)

  if (!context) {
    throw new Error('useLocaleBar() must be used within a LocaleBarProvider')
  }

  const { visible, show, hide, hasCookie } = context

  return {
    visible,
    show,
    hide,
    hasCookie,
  }
}

export default LocaleBarProvider
